// components/Header.tsx
"use client";

import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';
import {
  FaMapMarkerAlt,
  FaChevronDown,
  FaPhoneAlt,
  FaSearch,
  FaBars,
  FaTimes,
  FaHome,
  FaEnvelope,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
  FaCar,
  FaMotorcycle,
  FaTruck,
  FaPaw,
  FaGlobeAsia,
  FaBoxes,
  FaBuilding,
  FaPaperPlane,
  FaClipboardList,
  FaShieldAlt,
} from 'react-icons/fa';
import EnquiryForm from './EnquiryForm';

export default function Header() {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const overlayCloseBtnRef = useRef<HTMLButtonElement>(null);

  const popularCities = [
    'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Kolkata',
    'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow'
  ];

  const otherCities = [
    'Agra', 'Allahabad', 'Amritsar', 'Aurangabad', 'Bhopal', 'Bhubaneswar',
    'Chandigarh', 'Coimbatore', 'Dehradun', 'Faridabad', 'Ghaziabad', 'Goa',
    'Gurugram', 'Guwahati', 'Indore', 'Jabalpur', 'Jalandhar', 'Jammu',
    'Jodhpur', 'Kanpur', 'Kochi', 'Kota', 'Ludhiana', 'Madurai',
    'Mysore', 'Nagpur', 'Nashik', 'Navi Mumbai', 'Noida', 'Patna',
    'Raipur', 'Rajkot', 'Ranchi', 'Shimla', 'Srinagar', 'Surat',
    'Thane', 'Udaipur', 'Vadodara', 'Varanasi', 'Vijayawada', 'Visakhapatnam'
  ];

  const popularRoutes = [
    { from: 'Delhi', to: 'Bangalore' },
    { from: 'Delhi', to: 'Mumbai' },
    { from: 'Delhi', to: 'Kolkata' },
    { from: 'Delhi', to: 'Pune' },
    { from: 'Delhi', to: 'Hyderabad' },
    { from: 'Bangalore', to: 'Delhi' },
    { from: 'Mumbai', to: 'Delhi' },
    { from: 'Chennai', to: 'Bangalore' },
  ];

  const internationalCities = [
    'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata',
    'Hyderabad', 'Pune', 'Ahmedabad', 'Surat', 'Jaipur'
  ];

  const servicesList = [
    { name: 'Household Shifting', slug: 'household', icon: FaBoxes },
    { name: 'Office Shifting', slug: 'office', icon: FaBuilding },
    { name: 'Packers and Movers', slug: 'packers-movers', icon: FaTruck },
    { name: 'Car Transport', slug: 'car', icon: FaCar },
    { name: 'Bike Transport', slug: 'bike', icon: FaMotorcycle },
    { name: 'Warehouse Service', slug: 'warehouse', icon: FaClipboardList },
    { name: 'Loading and Unloading', slug: 'loading-unloading', icon: FaTruck },
    { name: 'Commercial Relocation', slug: 'commercial', icon: FaBuilding },
    { name: 'Local Relocation Service', slug: 'local', icon: FaTruck },
    { name: 'Goods Insurance Services', slug: 'insurance', icon: FaShieldAlt },
  ];

  const overlayServices = [
    'Domestic Moving', 'International Moving', 'Pet Relocation',
    'ODC Transportation', 'Car Carriers Transportation', 'IT Relocation',
    'Air Terminal Management', 'Corporate Office Relocation',
    'Supply Chain Management', 'Exim Cargo', 'Infrastructure'
  ];

  const forBusiness = [
    'Why Movezze', 'Clients', 'Feedback', 'Trucking Cube',
    'International Moving Guide', 'Photo Gallery', 'Expert Advice',
    'Corporate Office Relocation', 'Core Team', "Do or Don't", 'Corporate Moving'
  ];

  const forConsumers = [
    'About Us', 'Directors', 'Beware of Fake', 'Management',
    'Awards', 'Shifting Process', 'Achievements', 'Our USP',
    'Customer Care', 'Testimonials', 'Agarwal Movers Group',
    'Our History', 'Customer Review'
  ];

  const usefulLinks = [
    'Track Consignment', 'Online Payment', 'FAQs', 'Careers',
    'International Shifting', 'Safety Features', 'Vision & Mission',
    'TVC and Videos', 'Media Space', 'Press Release',
    'News and Events', 'Moving Checklist', 'Sitemap', 'Locations'
  ];

  const toggleOverlay = () => {
    setIsOverlayOpen(!isOverlayOpen);
    document.body.style.overflow = isOverlayOpen ? '' : 'hidden';
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    document.body.style.overflow = '';
  };

  const closeAllMenus = () => {
    closeOverlay();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isOverlayOpen) closeOverlay();
        if (isQuoteModalOpen) setIsQuoteModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOverlayOpen, isQuoteModalOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node) && isOverlayOpen) {
        closeOverlay();
      }
    };
    if (isOverlayOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverlayOpen]);

  return (
    <>
      {/* ===== MAIN HEADER ===== */}
      <header className="sticky top-0 z-40 shadow-sm bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            {/* LEFT: Logo + Location */}
            <div className="flex items-center gap-4">
              <Link href="/" className="flex items-center" onClick={closeAllMenus}>
                <img
                  src="/images/Movezze-Logistics-Private-Limited-Logo.svg"
                  alt="Movezze Logistics Private Limited"
                  className="h-7 md:h-9 w-auto"
                />
              </Link>

              {/* Location Mega Menu (Desktop) */}
              <div className="hidden lg:block relative group">
                <button className="flex items-center gap-1.5 shadow-sm bg-gray-100 hover:bg-gray-200 text-[#343434] px-3 py-1.5 rounded-lg transition text-sm font-medium">
                  <FaMapMarkerAlt className="text-xs" />
                  <span>Location</span>
                  <FaChevronDown className="text-[10px]" />
                </button>
                <div className="absolute top-full left-0 w-[900px] bg-white shadow-xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-50 border border-gray-100">
                  <div className="p-5">
                    <div className="flex gap-6">
                      <div className="w-[70%]">
                        <div className="mb-5">
                          <h4 className="font-bold text-[#292929] text-sm mb-2">Popular Cities</h4>
                          <div className="grid grid-cols-5 gap-x-3 gap-y-1 text-sm">
                            {popularCities.map((city) => (
                              <Link key={city} href={`/${city.toLowerCase()}`} className="text-[#343434] hover:text-[#f62b1e] transition">
                                {city}
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#292929] text-sm mb-2">Other Cities</h4>
                          <div className="grid grid-cols-5 gap-x-3 gap-y-1 text-sm">
                            {otherCities.map((city) => (
                              <Link key={city} href={`/${city.toLowerCase()}`} className="text-[#343434] hover:text-[#f62b1e] transition">
                                {city}
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div className="mt-2">
                          <h4 className="font-bold text-[#292929] text-sm mb-2">Popular Routes</h4>
                          <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm">
                            {popularRoutes.map((route, index) => (
                              <Link key={index} href={`/${route.from.toLowerCase()}-to-${route.to.toLowerCase()}`} className="text-[#343434] hover:text-[#f62b1e] transition">
                                {route.from} → {route.to}
                              </Link>
                            ))}
                          </div>
                          <div className="mt-2 text-left">
                            <Link href="/all-routes" className="text-xs text-[#f62b1e] font-medium hover:text-[#df261a] transition">
                              View all →
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="w-[30%] border-l border-gray-100 pl-6">
                        <h4 className="font-bold text-[#292929] text-sm mb-2">International Relocation</h4>
                        <div className="flex flex-col gap-1 text-sm">
                          {internationalCities.map((city) => (
                            <Link key={city} href={`/international-relocation-from-${city.toLowerCase()}`} className="text-[#343434] hover:text-[#f62b1e] transition">
                              From {city}
                            </Link>
                          ))}
                        </div>
                        <div className="mt-2 text-left">
                          <Link href="/international-relocation" className="text-xs text-[#f62b1e] font-medium hover:text-[#df261a] transition">
                            All international →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="relative group">
                <button className="text-[#343434] hover:text-[#f62b1e] font-medium flex items-center gap-1 transition text-sm">
                  Services <FaChevronDown className="text-[10px]" />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-56 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-50 border border-gray-100">
                  <div className="py-2 text-sm">
                    {servicesList.map((service, index) => {
                      const Icon = service.icon;
                      return (
                        <Link
                          key={index}
                          href={`/services/${service.slug}`}
                          className="flex items-center gap-2 px-5 py-2 text-[#343434] hover:bg-gray-100 hover:text-[#f62b1e] transition"
                        >
                          <Icon className="text-sm" />
                          {service.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              <Link
                href="/contact"
                className="text-[#343434] hover:text-[#f62b1e] transition text-sm font-medium"
              >
                Contact
              </Link>

              <div className="flex items-center gap-2 border-r border-gray-200 pr-4">
                <FaPhoneAlt className="text-[#343434] text-sm" />
                <a
                  href="tel:+918130880900"
                  className="text-[#343434] font-medium text-sm hover:text-[#f62b1e]"
                >
                  +91 8130 880 900
                </a>
              </div>

              {/* Get a Quote Button (Desktop) – Red accent */}
              <button
                onClick={() => setIsQuoteModalOpen(true)}
                className="shine-btn bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold px-4 py-1.5 rounded-full text-xs transition shadow-sm relative overflow-hidden flex items-center gap-1.5"
              >
                <FaEnvelope className="text-white text-xs" />
                Get a Quote
              </button>

              <button
                onClick={toggleOverlay}
                className="text-[#343434] hover:text-[#f62b1e] transition focus:outline-none ml-2"
                aria-label="Toggle menu"
              >
                <FaBars size={20} />
              </button>
            </div>

            {/* MOBILE: Icons + Hamburger */}
            <div className="flex lg:hidden items-center gap-3">
              <button
                onClick={() => setIsQuoteModalOpen(true)}
                className="shine-btn bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold px-3 py-1 rounded-md text-xs transition shadow-sm relative overflow-hidden flex items-center gap-1"
              >
                <FaPaperPlane className="text-white text-[10px]" />
                Get Quote
              </button>
              <button
                onClick={toggleOverlay}
                className="text-[#343434] hover:text-[#f62b1e] transition focus:outline-none"
                aria-label="Toggle menu"
              >
                {isOverlayOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ===== FULL OVERLAY MENU ===== */}
      <div
        ref={overlayRef}
        className={`fixed inset-0 z-50 bg-white overflow-y-auto transition-opacity duration-300 ease-in-out ${
          isOverlayOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="sticky top-0 bg-white z-10 flex justify-end p-4 border-b border-gray-100">
          <button
            ref={overlayCloseBtnRef}
            onClick={closeOverlay}
            className="text-2xl text-gray-700 hover:text-[#f62b1e] transition"
          >
            <FaTimes />
          </button>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="lg:w-1/4 flex-shrink-0">
              <div className="space-y-4">
                <Link href="/" className="block" onClick={closeAllMenus}>
                  <img
                    src="/images/Movezze-Logistics-Private-Limited-Logo.svg"
                    alt="Movezze Logistics Private Limited"
                    className="h-10 w-auto"
                  />
                </Link>
                <div className="space-y-2 text-sm text-[#343434]">
                  <p className="flex items-center gap-2">
                    <FaPhoneAlt className="text-gray-400 w-5" /> +91 8130 880 900
                  </p>
                  <p className="flex items-center gap-2">
                    <FaEnvelope className="text-gray-400 w-5" /> info@movezze.com
                  </p>
                  <p className="flex items-start gap-2">
                    <FaMapMarkerAlt className="text-gray-400 w-5 mt-1" />
                    <span>Delhi NCR, India</span>
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <a href="https://www.facebook.com/movezze" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#f62b1e] transition text-xl"><FaFacebookF /></a>
                  <a href="https://x.com/movezze" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#f62b1e] transition text-xl"><FaTwitter /></a>
                  <a href="https://www.instagram.com/movezze/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#f62b1e] transition text-xl"><FaInstagram /></a>
                  <a href="https://www.youtube.com/@movezze" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#f62b1e] transition text-xl"><FaYoutube /></a>
                  <a href="#" className="text-gray-400 hover:text-[#f62b1e] transition text-xl"><FaLinkedinIn /></a>
                </div>
              </div>
            </div>

            <div className="lg:w-3/4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <div>
                  <h3 className="font-semibold text-[#292929] text-sm uppercase tracking-wider mb-3">Our Services</h3>
                  <ul className="space-y-2 text-sm">
                    {overlayServices.map((item, i) => (
                      <li key={i}>
                        <Link
                          href="#"
                          className="text-[#343434] hover:text-[#f62b1e] transition"
                          onClick={closeOverlay}
                        >
                          {item}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-[#292929] text-sm uppercase tracking-wider mb-3">For Business</h3>
                  <ul className="space-y-2 text-sm">
                    {forBusiness.map((item, i) => (
                      <li key={i}>
                        <Link
                          href="#"
                          className="text-[#343434] hover:text-[#f62b1e] transition"
                          onClick={closeOverlay}
                        >
                          {item}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-[#292929] text-sm uppercase tracking-wider mb-3">For Consumers</h3>
                  <ul className="space-y-2 text-sm">
                    {forConsumers.map((item, i) => (
                      <li key={i}>
                        <Link
                          href="#"
                          className="text-[#343434] hover:text-[#f62b1e] transition"
                          onClick={closeOverlay}
                        >
                          {item}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-[#292929] text-sm uppercase tracking-wider mb-3">Useful Links</h3>
                  <ul className="space-y-2 text-sm">
                    {usefulLinks.map((item, i) => (
                      <li key={i}>
                        <Link
                          href="#"
                          className="text-[#343434] hover:text-[#f62b1e] transition"
                          onClick={closeOverlay}
                        >
                          {item}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== QUOTE MODAL ===== */}
      {isQuoteModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setIsQuoteModalOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-[#f62b1e] z-10"
            >
              <FaTimes className="w-6 h-6" />
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-[#292929] mb-4">Get an Instant Quote</h2>
              <EnquiryForm showServiceDropdown />
            </div>
          </div>
        </div>
      )}

      {/* ===== TRUST MARQUEE ===== */}
      <div className="py-2.5 overflow-hidden bg-[#f8f9fa]">
        <div className="marquee-track flex gap-10 md:gap-14 whitespace-nowrap text-sm font-medium text-[#343434]">
          <span><span className="text-[#f62b1e]">✓</span> 100% Insured Moving</span>
          <span><span className="text-[#f62b1e]">✓</span> No Hidden Charges</span>
          <span><span className="text-[#f62b1e]">✓</span> Real-time GPS Tracking</span>
          <span><span className="text-[#f62b1e]">✓</span> 100+ Cities Covered</span>
          <span><span className="text-[#f62b1e]">✦</span> 4.9 ★ Google Rating</span>
          <span><span className="text-[#f62b1e]">✓</span> 24/7 Customer Support</span>
          <span><span className="text-[#f62b1e]">✓</span> Verified Movers</span>
          <span><span className="text-[#f62b1e]">✓</span> Door-to-Door Service</span>
          <span><span className="text-[#f62b1e]">✓</span> Transparent Pricing</span>
          <span><span className="text-[#f62b1e]">✓</span> Expert Packers</span>
          {/* Duplicate for seamless loop */}
          <span><span className="text-[#f62b1e]">✓</span> 100% Insured Moving</span>
          <span><span className="text-[#f62b1e]">✓</span> No Hidden Charges</span>
          <span><span className="text-[#f62b1e]">✓</span> Real-time GPS Tracking</span>
          <span><span className="text-[#f62b1e]">✓</span> 100+ Cities Covered</span>
          <span><span className="text-[#f62b1e]">✦</span> 4.9 ★ Google Rating</span>
          <span><span className="text-[#f62b1e]">✓</span> 24/7 Customer Support</span>
          <span><span className="text-[#f62b1e]">✓</span> Verified Movers</span>
          <span><span className="text-[#f62b1e]">✓</span> Door-to-Door Service</span>
          <span><span className="text-[#f62b1e]">✓</span> Transparent Pricing</span>
          <span><span className="text-[#f62b1e]">✓</span> Expert Packers</span>
        </div>
      </div>

      <style>{`
        .marquee-track {
          display: flex;
          animation: marqueeScroll 35s linear infinite;
          width: max-content;
        }
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (max-width: 768px) {
          .marquee-track {
            animation-duration: 25s;
            gap: 1.5rem;
          }
        }
        @media (max-width: 480px) {
          .marquee-track {
            animation-duration: 25s;
            gap: 1rem;
            font-size: 0.75rem;
          }
        }

        /* ✨ Shine Animation for Get a Quote button */
        .shine-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transform: skewX(-25deg);
          animation: shine 3.5s infinite ease-in-out;
        }

        @keyframes shine {
          0% { left: -100%; }
          20% { left: 100%; }
          100% { left: 100%; }
        }

        .shine-btn:hover::before {
          animation: none;
        }
      `}</style>
    </>
  );
}